import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Tag, Button, Space, Statistic, Row, Col, message, Tooltip, Badge, Input, Modal, Tabs, Select, Descriptions, Spin, Popconfirm } from 'antd';
import { LaptopOutlined, ReloadOutlined, SyncOutlined, WifiOutlined, ApiOutlined, DesktopOutlined, BuildOutlined, HddOutlined, SearchOutlined, DeleteOutlined, DeleteTwoTone } from '@ant-design/icons';
import { accountApi, clientApi, tradingApi, groupApi } from '../services/api';
import { useAuth } from '../utils/auth';
import { useSearchParams } from 'react-router-dom';
import config from '../config';

export default function AccountsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'admin' || user?.is_super_admin === 1;
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [todayOrders, setTodayOrders] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const [filterStatus, setFilterStatus] = useState('online');
  const [clientSearch, setClientSearch] = useState('');
  const _now = new Date(); const _h = _now.getHours(); const _m = _now.getMinutes();
  const _tn = _h * 100 + _m;
  const isTrading = _tn >= 930 && _tn <= 1130 || _tn >= 1300 && _tn <= 1500;
  const [fills, setFills] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPage, setClientPage] = useState({ current: 1, pageSize: 10 });
  const [positions, setPositions] = useState([]);
  const [apiDown, setApiDown] = useState(false);
  const [renameModal, setRenameModal] = useState({ open: false, clientId: null, name: '', sunflower_account: '', sunflower_pass: '', broker: '' });
  const [activeTab, setActiveTab] = useState('positions');
  const [tradeForm, setTradeForm] = useState({ stockCode: '', stockName: '', price: '', quantity: '', direction: 'buy', orderType: 'limit', availableQty: 0, currentPrice: 0 });
  const [priceLevelMode, setPriceLevelMode] = useState(null); // null | 'buy' | 'sell'
  const [quoteMap, setQuoteMap] = useState({});
  const [groupClientSet, setGroupClientSet] = useState(null); // null=不过滤

  // 自动查询股票行情
  useEffect(() => {
    const code = tradeForm.stockCode?.replace(/\D/g, '');
    if (code?.length === 6) {
      tradingApi.getQuote(code).then(res => {
        if (res.success && res.data) {
          const d = res.data;
          setTradeForm(prev => ({
            ...prev,
            stockName: d.stock_name || '',
            price: d.current_price > 0 ? String(d.current_price) : prev.price,
          }));
        }
      }).catch(() => {});
    }
  }, [tradeForm.stockCode]);

  const loadTodayOrders = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await clientApi.getTodayOrders(cid);
      if (res.success) {
        setTodayOrders(res.data.today_orders || []);
      }
    } catch { /* ignore */ }
  };

  const loadScreenshots = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await clientApi.getScreenshots(cid);
      if (res.success) setScreenshots(res.data || []);
    } catch { /* ignore */ }
  };

  const onTabChange = (key) => {
    setActiveTab(key);
    if (key === 'todayOrders') loadTodayOrders();
    else if (key === 'screenshots') loadScreenshots();
    else if (key === 'orders') loadOrders();
    else if (key === 'fills') loadFills();
    else if (key === 'deliveries') loadDeliveries();
    else if (key === 'positions') loadPositions();
    else if (key === 'accounts') loadAccounts();
  };

  // 搜索过滤
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [orderDirection, setOrderDirection] = useState('');
  const [fillSearch, setFillSearch] = useState('');
  const [fillDirection, setFillDirection] = useState('');
  const [deliverySearch, setDeliverySearch] = useState('');
  const [deliveryDirection, setDeliveryDirection] = useState('');
  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');

  useEffect(() => {
    loadData();
    // 从分组界面跳转而来时，读取 client_id 参数并自动筛选该客户端
    const cid = searchParams.get('client_id');
    if (cid) {
      setClientSearch(cid);
      setFilterStatus('');
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedClient) {
      loadAccounts();
      loadPositions();
      loadOrders();
      loadFills();
      loadTodayOrders();
      loadScreenshots();
    }
  }, [selectedClient]);

  const loadData = async () => {
    try {
      const clientRes = await clientApi.getOnline();
      if (clientRes.success) { setClients(clientRes.data || []); setApiDown(false); }
    } catch { setApiDown(true); }
  };

  const loadPositions = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await accountApi.getAllPositions({ client_id: cid });
      if (res.success) {
        const data = res.data || [];
        setPositions(data);
        // 获取实时行情（补零到6位）
        const codes = [...new Set(data.map(p => p.stock_code).filter(Boolean).map(c => String(c).padStart(6, '0')))];
        if (codes.length > 0) {
          tradingApi.getQuotes(codes).then(qres => {
            if (qres.success) {
              const m = {};
              (qres.data || []).forEach(q => { if (q.stock_code) m[q.stock_code] = q; });
              setQuoteMap(m);
            }
          }).catch(() => {});
        }
      }
    } catch { /* ignore */ }
  };

  const loadAccounts = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await accountApi.getAll({ client_id: cid });
      if (res.success) {
        setAccounts(res.data || []);
      }
    } catch { /* ignore */ }
  };

  const loadOrders = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await tradingApi.getOrders({ client_id: cid, pageSize: 500 });
      if (res.success) {
        setOrders(res.data.orders || []);
      }
    } catch { /* ignore */ }
  };

  const loadFills = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await tradingApi.getFills({ client_id: cid, pageSize: 500 });
      console.log('[历史成交]', res);
      if (res.success) {
        setFills(res.data?.fills || []);
      }
    } catch { /* ignore */ }
  };

  const loadDeliveries = async (cid) => {
    try {
      if (!cid) cid = selectedClient?.id;
      if (!cid) return;
      const res = await clientApi.getDeliveries({ client_id: cid });
      if (res.success) setDeliveries((res.data && res.data.deliveries) || []);
    } catch { /* ignore */ }
  };

  const handleTrade = async (direction) => {
    if (!tradeForm.stockCode || !tradeForm.quantity) {
      message.warning('请填写完整交易信息');
      return;
    }
    const isMarket = tradeForm.orderType === 'market';
    const price = isMarket ? 0 : parseFloat(tradeForm.price);
    const qty = parseInt(tradeForm.quantity);
    if (qty < 100 || qty % 100 !== 0) {
      message.warning('委托数量必须是100的倍数且不小于100股');
      return;
    }
    if (!isMarket && !priceLevelMode) {
      if (!tradeForm.price || isNaN(price) || price <= 0) {
        message.warning('请输入有效委托价格');
        return;
      }
      const amount = price * qty;
      if (direction === 'buy') {
        const available = accounts[0]?.available || 0;
        if (amount > available) {
          message.warning(`资金不足：需 ${amount.toFixed(2)} 元，可用 ${available.toFixed(2)} 元`);
          return;
        }
      }
    } else {
      if (direction === 'sell') {
        const available = tradeForm.availableQty;
        if (qty > available) {
          message.warning(`股票不足：需 ${qty} 股，可用 ${available} 股`);
          return;
        }
      }
    }

    setSyncingModal({ open: true, label: direction === 'buy' ? '买入' : '卖出' });
    try {
      let res;
      if (priceLevelMode === 'buy') {
        res = await tradingApi.buyAtPriceLevel({
          account_id: accounts[0]?.id || 'ths_main', client_id: selectedClient?.id || '',
          stock_code: tradeForm.stockCode, stock_name: tradeForm.stockName,
          quantity: qty, ask_level: 2,
        });
      } else if (priceLevelMode === 'sell') {
        res = await tradingApi.sellAtPriceLevel({
          account_id: accounts[0]?.id || 'ths_main', client_id: selectedClient?.id || '',
          stock_code: tradeForm.stockCode, stock_name: tradeForm.stockName,
          quantity: qty, bid_level: 2,
        });
      } else {
        const fn = direction === 'buy' ? tradingApi.buy : tradingApi.sell;
        const params = {
          account_id: accounts[0]?.id || 'ths_main',
          client_id: selectedClient?.id || '',
          stock_code: tradeForm.stockCode,
          stock_name: tradeForm.stockName,
          quantity: qty,
          order_type: tradeForm.orderType,
        };
        if (!isMarket) params.price = price;
        res = await fn(params);
      }
      setSyncingModal({ open: false, label: '' });
      setPriceLevelMode(null);
      if (res.success) message.success(`${direction === 'buy' ? '买入' : '卖出'}委托已提交`);
      else message.error(res.message || '交易失败');
    } catch { setSyncingModal({ open: false, label: '' }); message.error('交易请求失败'); }
  };

  const handleBuyAtBidLevel = async (bidLevel) => {
    if (!tradeForm.stockCode || !tradeForm.quantity) {
      message.warning('请填写股票代码和数量'); return;
    }
    const qty = parseInt(tradeForm.quantity);
    if (qty < 100 || qty % 100 !== 0) {
      message.warning('委托数量必须是100的倍数且不小于100股'); return;
    }
    setSyncingModal({ open: true, label: `买${bidLevel}价买入` });
    try {
      const res = await tradingApi.buyAtPriceLevel({
        account_id: accounts[0]?.id || 'ths_main',
        client_id: selectedClient?.id || '',
        stock_code: tradeForm.stockCode,
        stock_name: tradeForm.stockName,
        quantity: qty,
        ask_level: bidLevel,
      });
      setSyncingModal({ open: false, label: '' });
      if (res.success) message.success(`买${bidLevel}价委托已提交`);
      else message.error(res.message || '交易失败');
    } catch { setSyncingModal({ open: false, label: '' }); message.error('交易请求失败'); }
  };

  const handleSellAtAskLevel = async (askLevel) => {
    if (!tradeForm.stockCode || !tradeForm.quantity) {
      message.warning('请填写股票代码和数量'); return;
    }
    const qty = parseInt(tradeForm.quantity);
    if (qty < 100 || qty % 100 !== 0) {
      message.warning('委托数量必须是100的倍数且不小于100股'); return;
    }
    setSyncingModal({ open: true, label: `卖${askLevel}价卖出` });
    try {
      const res = await tradingApi.sellAtPriceLevel({
        account_id: accounts[0]?.id || 'ths_main',
        client_id: selectedClient?.id || '',
        stock_code: tradeForm.stockCode,
        stock_name: tradeForm.stockName,
        quantity: qty,
        bid_level: askLevel,
      });
      setSyncingModal({ open: false, label: '' });
      if (res.success) message.success(`卖${askLevel}价委托已提交`);
      else message.error(res.message || '交易失败');
    } catch { setSyncingModal({ open: false, label: '' }); message.error('交易请求失败'); }
  };

  const onSelectPosition = (r) => {
    const key = String(r.stock_code).padStart(6, '0');
    const q = quoteMap[key];
    const price = (q?.current_price) || r.current_price || 0;
    setTradeForm({ orderType: 'limit',
      stockCode: r.stock_code,
      stockName: r.stock_name,
      price: price.toString(),
      quantity: r.available_qty?.toString() || r.quantity?.toString() || '',
      direction: 'sell',
      availableQty: r.available_qty || r.quantity || 0,
      currentPrice: price,
    });
  };

  const [syncingModal, setSyncingModal] = useState({ open: false, label: '' });
  const [syncBatch, setSyncBatch] = useState({ open: false, progress: '', done: false });
  // 同步弹窗（confirm: 展示待同步列表 | running: 执行中 | done: 完成）
  const [syncResult, setSyncResult] = useState({
    open: false, mode: 'confirm', title: '', type: 'positions',
    status: 'idle', success: 0, fail: 0, total: 0, results: [],
    clientList: [], message: '',
  });

  // 轮询同步任务状态
  const pollSyncTask = (taskId) => {
    const timer = setInterval(async () => {
      try {
        const res = await clientApi.getSyncTask(taskId);
        if (!res.success) { clearInterval(timer); return; }
        const t = res.data;
        setSyncResult(prev => ({
          ...prev,
          mode: 'running', status: t.status,
          success: t.success, fail: t.fail, total: t.total,
          results: t.results || [],
        }));
        if (t.status === 'done') {
          clearInterval(timer);
          // 完成后刷新数据
          if (t.type === 'positions') { loadAccounts(); loadPositions(); loadAssetStats(); }
          else if (t.type === 'deliveries') { if (selectedClient?.id) loadDeliveries(); }
          else { loadTodayOrders(); }
        }
      } catch { clearInterval(timer); }
    }, 2000);
  };

  // 打开同步确认弹窗，显示在线/离线客户端列表
  const openSyncConfirm = (type, title) => {
    const onlineList = clients.filter(c => c.status === 'online')
      .map(c => ({ client_id: c.id, client_name: c.name || c.id }));
    setSyncResult({
      open: true, mode: 'confirm', title, type,
      status: 'idle', success: 0, fail: 0, total: onlineList.length,
      results: [], clientList: onlineList, message: '',
    });
  };

  // 确认后真正执行同步
  const executeSync = async () => {
    const onlineList = syncResult.clientList;
    if (onlineList.length === 0) {
      message.warning('没有在线客户端');
      setSyncResult(prev => ({ ...prev, mode: 'done', message: '没有在线客户端' }));
      return;
    }
    setSyncResult(prev => ({ ...prev, mode: 'running', status: 'running', success: 0, fail: 0, results: [] }));
    try {
      const res = syncResult.type === 'positions'
        ? await clientApi.syncAllData()
        : syncResult.type === 'deliveries'
          ? await clientApi.syncAllDeliveries()
          : await clientApi.syncAllTodayOrders();
      if (res.success && res.data?.task_id) {
        pollSyncTask(res.data.task_id);
      } else {
        setSyncResult(prev => ({ ...prev, mode: 'done', status: 'done', message: res.message || '同步失败' }));
      }
    } catch {
      setSyncResult(prev => ({ ...prev, mode: 'done', status: 'done', message: '同步请求失败' }));
    }
  };

  const syncAllAccounts = async () => {
    const online = clients.filter(c => c.status === 'online');
    if (online.length === 0) return;
    setSyncBatch({ open: true, progress: '准备中...', done: false });
    for (let i = 0; i < online.length; i++) {
      const c = online[i];
      setSyncBatch(prev => ({ ...prev, progress: `正在同步 ${c.name || c.id} (${i + 1}/${online.length})...` }));
      try {
        const res = await clientApi.syncAccount(c.id);
        if (!res.success) console.warn(`同步 ${c.id} 失败:`, res.message);
      } catch { console.warn(`同步 ${c.id} 异常`); }
    }
    setSyncBatch({ open: true, progress: '全部完成', done: true });
    loadAccounts();
    setTimeout(() => setSyncBatch({ open: false, progress: '', done: false }), 1500);
  };

  const syncAllPositions = async () => {
    const online = clients.filter(c => c.status === 'online');
    if (online.length === 0) return;
    setSyncBatch({ open: true, progress: '准备中...', done: false });
    for (let i = 0; i < online.length; i++) {
      const c = online[i];
      setSyncBatch(prev => ({ ...prev, progress: `正在同步 ${c.name || c.id} 持仓 (${i + 1}/${online.length})...` }));
      try {
        const res = await clientApi.syncCurrentPositions(c.id);
        if (!res.success) console.warn(`同步持仓 ${c.id} 失败:`, res.message);
      } catch { console.warn(`同步持仓 ${c.id} 异常`); }
    }
    setSyncBatch({ open: true, progress: '全部完成', done: true });
    loadPositions();
    setTimeout(() => setSyncBatch({ open: false, progress: '', done: false }), 1500);
  };

  const syncOp = async (clientId, op, label, reloadOp) => {
    setSyncingModal({ open: true, label });
    try {
      const res = await clientApi[op](clientId);
      setSyncingModal({ open: false, label: '' });
      if (res.success) {
        message.success(`${label}成功`);
        if (reloadOp) setTimeout(reloadOp, 2000);
      } else message.error(res.message || `${label}失败`);
    } catch {
      setSyncingModal({ open: false, label: '' });
      message.error(`${label}失败，请检查客户端是否在线`);
    }
  };

  const handleRename = async () => {
    if (!renameModal.name.trim()) return;
    try {
      const res = await clientApi.rename(renameModal.clientId, {
        name: renameModal.name.trim(),
        sunflower_account: renameModal.sunflower_account.trim(),
        sunflower_pass: renameModal.sunflower_pass.trim(),
        broker: renameModal.broker.trim(),
      });
      if (res.success) {
        message.success('已保存');
        setRenameModal({ open: false, clientId: null, name: '', sunflower_account: '', sunflower_pass: '', broker: '' });
        loadData();
      }
    } catch { message.error('修改失败'); }
  };

  // 日期过滤
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30*24*60*60*1000).toISOString().split('T')[0];
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [fillDateFrom, setFillDateFrom] = useState('');
  const [fillDateTo, setFillDateTo] = useState('');

  // 搜索过滤
  const filteredOrders = useMemo(() => {
    let list = orders;
    if (orderSearch) {
      const s = orderSearch.toLowerCase();
      list = list.filter(r => (r.stock_code || '').toLowerCase().includes(s) || (r.stock_name || '').toLowerCase().includes(s));
    }
    if (orderStatus) list = list.filter(r => r.status === orderStatus);
    if (orderDirection) list = list.filter(r => r.direction === orderDirection);
    if (orderDateFrom) list = list.filter(r => (r.order_date || '').replace(/-/g, '') >= orderDateFrom.replace(/-/g, ''));
    if (orderDateTo) list = list.filter(r => (r.order_date || '').replace(/-/g, '') <= orderDateTo.replace(/-/g, ''));
    return list;
  }, [orders, orderSearch, orderStatus, orderDirection, orderDateFrom, orderDateTo]);

  const filteredFills = useMemo(() => {
    let list = fills;
    if (fillSearch) {
      const s = fillSearch.toLowerCase();
      list = list.filter(r => (r.stock_code || '').toLowerCase().includes(s) || (r.stock_name || '').toLowerCase().includes(s));
    }
    if (fillDirection) list = list.filter(r => r.direction === fillDirection);
    if (fillDateFrom) list = list.filter(r => (r.trade_date || '').replace(/-/g, '') >= fillDateFrom.replace(/-/g, ''));
    if (fillDateTo) list = list.filter(r => (r.trade_date || '').replace(/-/g, '') <= fillDateTo.replace(/-/g, ''));
    return list;
  }, [fills, fillSearch, fillDirection, fillDateFrom, fillDateTo]);

  const filteredDeliveries = useMemo(() => {
    let list = deliveries;
    if (deliverySearch) {
      const s = deliverySearch.toLowerCase();
      list = list.filter(r => (r.stock_code || '').toLowerCase().includes(s) || (r.stock_name || '').toLowerCase().includes(s));
    }
    if (deliveryDirection) list = list.filter(r => r.direction === deliveryDirection);
    // trade_date 存的是 YYYYMMDD，日期输入 YYYY-MM-DD 去掉连字符后比较
    if (deliveryDateFrom) {
      const f = deliveryDateFrom.replace(/-/g, '');
      list = list.filter(r => (r.trade_date || '') >= f);
    }
    if (deliveryDateTo) {
      const t = deliveryDateTo.replace(/-/g, '');
      list = list.filter(r => (r.trade_date || '') <= t);
    }
    return list;
  }, [deliveries, deliverySearch, deliveryDirection, deliveryDateFrom, deliveryDateTo]);

  const clientColumns = [
    { title: '名称', dataIndex: 'name', width: 130, render: (v, r) => (
      <span><code style={{ fontSize: 11, marginRight: 6 }}>{r.id}</code><a onClick={(e) => { e.stopPropagation(); setRenameModal({ open: true, clientId: r.id, name: v || '', sunflower_account: r.sunflower_account || '', sunflower_pass: r.sunflower_pass || '', broker: r.broker || '' }); }}>{v}</a></span>
    )},
    { title: '券商', dataIndex: 'broker', width: 80, render: v => v ? <Tag>{v}</Tag> : '-' },
    { title: '状态', dataIndex: 'status', width: 65, render: v => (
      v === 'online' ? <Tag color="green">在线</Tag> : <Tag color="default">离线</Tag>
    )},
    { title: '同花顺', width: 70, render: (_, r) => {
      if (r.ths_status === 'ready') return <Tag color="green">就绪</Tag>;
      if (r.ths_status === 'disconnected') return <Tag color="red">未连接</Tag>;
      return r.status === 'online' ? <Tag color="default">等待中</Tag> : <Tag color="default">离线</Tag>;
    }},
    { title: '版本', dataIndex: 'version', width: 55, render: v => v ? <Tag>{v}</Tag> : '-' },
    { title: 'MAC', dataIndex: 'mac_addr', width: 120, render: v => v ? <code style={{ fontSize: 11 }}>{v}</code> : '-' },
    {
      title: '操作', width: 300, render: (_, r) => {
        const thsOff = r.ths_status === 'disconnected';
        return (
        <Space size={4} wrap>
          {r.ths_status === 'ready' && <Button size="small" disabled={r.status !== 'online'} onClick={() => syncOp(r.id, 'syncAllClientData', '资金+持仓', () => { loadAccounts(); loadPositions(); })}>同步资金持仓</Button>}
          {r.ths_status === 'ready' && <Button size="small" disabled={r.status !== 'online'} onClick={() => syncOp(r.id, 'syncTodayOrders', '当日委托', loadTodayOrders)}>当日委托</Button>}
          {r.ths_status === 'ready' && <Button size="small" disabled={r.status !== 'online'} onClick={() => syncOp(r.id, 'syncFills', '同步历史委托', loadOrders)}>历史委托</Button>}
          {r.ths_status === 'ready' && <Button size="small" disabled={r.status !== 'online'} onClick={() => syncOp(r.id, 'syncHistory', '同步历史成交', loadFills)}>历史成交</Button>}
          {r.ths_status === 'ready' && <Button size="small" type="primary" ghost disabled={r.status !== 'online'} onClick={() => {
            setSelectedClient(r);
            setActiveTab('deliveries');
            loadDeliveries(r.id);
            syncOp(r.id, 'syncDeliveries', '同步交割单(近一年)', () => loadDeliveries(r.id));
          }}>交割单</Button>}
          <Button size="small" disabled={r.status !== 'online'} onClick={async () => { message.loading('截图发送中...'); const r2 = await clientApi.triggerScreenshot(r.id); message.destroy(); if (r2.success) message.success('截图成功'); else message.error(r2.message || '截图失败') }}>截图</Button>
        </Space>
      );
    }},
  ];


  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const saveAccount = async () => {
    if (!editingId) return;
    try {
      const res = await accountApi.update(editingId, editForm);
      if (res.success) { message.success('已保存'); setEditingId(null); loadAccounts(); }
      else message.error(res.message || '保存失败');
    } catch { message.error('保存失败'); }
  };

  const editAccount = (r) => {
    setEditingId(r.id);
    setEditForm({ name: r.name, account_no: r.account_no });
  };

  const accountColumns = [
    { title: '名称', dataIndex: 'name', width: 90, render: (v, r) =>
      editingId === r.id
        ? <Input size="small" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} style={{ width: 80 }} />
        : <a onClick={() => editAccount(r)}>{v}</a>
    },
    { title: '资金账号', dataIndex: 'account_no', width: 110, render: (v, r) =>
      editingId === r.id
        ? <Input size="small" value={editForm.account_no} onChange={e => setEditForm({...editForm, account_no: e.target.value})} style={{ width: 100 }} />
        : <span onClick={() => editAccount(r)} style={{ cursor: 'pointer' }}>{v}</span>
    },
    { title: '总资产', dataIndex: 'total_asset', width: 100, render: v => <span style={{ fontWeight: 600 }}>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
    { title: '可用金额', dataIndex: 'available', width: 90, render: v => <span style={{ color: '#27ae60' }}>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
    { title: '股票市值', dataIndex: 'market_value', width: 100, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
    { title: '冻结', dataIndex: 'frozen', width: 70, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
  ];

  const doCancel = async (orderId) => {
    setSyncingModal({ open: true, label: '撤单' });
    try {
      const res = await tradingApi.cancel(orderId);
      setSyncingModal({ open: false, label: '' });
      if (res.success) { message.success('撤单成功'); loadTodayOrders(); loadOrders(); }
      else message.error(res.message || '撤单失败');
    } catch { setSyncingModal({ open: false, label: '' }); message.error('撤单失败'); }
  };

  const orderColumns = [
    { title: '日期', dataIndex: 'order_date', width: 80, render: v => v || '-' },
    { title: '时间', dataIndex: 'order_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 110, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
    { title: '方向', dataIndex: 'direction', width: 50, render: v => {
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '价格', dataIndex: 'price', width: 65, render: v => v?.toFixed(2) || '市价' },
    { title: '数量', dataIndex: 'quantity', width: 65, render: v => v?.toLocaleString() },
    { title: '成交', dataIndex: 'filled_qty', width: 65, render: v => v?.toLocaleString() || 0 },
    { title: '状态', dataIndex: 'status', width: 65, render: v => {
      const colorMap = { '已成': 'success', '已撤': 'default', '已报': 'processing', '部成': 'warning', '废单': 'error' };
      const color = Object.entries(colorMap).find(([k]) => v?.includes(k))?.[1] || 'default';
      return <Tag color={color}>{v || '-'}</Tag>;
    }},
    { title: '操作', width: 60, render: (_, r) => {
      const canCancel = ['已报', '部成', 'submitted', 'partially_filled'].some(s => (r.status || '').includes(s));
      return canCancel ? <Button size="small" danger onClick={(e) => { e.stopPropagation(); doCancel(r.id); }}>撤单</Button> : null;
    }},
    { title: '市场', dataIndex: 'market', width: 65, render: v => v ? <Tag>{v}</Tag> : '-' },
  ];

  // 历史委托不显示操作列
  const historyOrderColumns = orderColumns.filter(col => col.title !== '操作');

  const posColumns = [
    { title: '股票', key: 'stock', width: 120, render: (_, r) => {
      const gray = (r.quantity || 0) === 0;
      return <span style={{ color: gray ? '#ccc' : undefined }}><strong>{r.stock_name}</strong><br /><small>{r.stock_code}</small></span>;
    }},
    { title: '股票余额', dataIndex: 'quantity', width: 80, render: v => v?.toLocaleString() },
    { title: '可用余额', dataIndex: 'available_qty', width: 80, render: v => v?.toLocaleString() },
    { title: '冻结', dataIndex: 'frozen_qty', width: 60, render: v => v?.toLocaleString() || 0 },
    { title: '成本价', dataIndex: 'cost_price', width: 80, render: v => v?.toFixed(3) },
    { title: '市价', key: 'live_price', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      if (q) {
        const color = q.change_percent > 0 ? '#e74c3c' : q.change_percent < 0 ? '#27ae60' : '#333';
        return <span style={{ fontWeight: 600, color }}>{q.current_price.toFixed(2)}</span>;
      }
      return r.current_price?.toFixed(2) || '-';
    }},
    { title: '市值', dataIndex: 'market_value', width: 90, render: v => v?.toLocaleString() },
    { title: '参考盈亏', key: 'live_pl', width: 100, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      const qty = r.quantity || 0;
      const pl = cost > 0 ? (price - cost) * qty : (r.profit_loss || 0);
      return (
        <span style={{ color: pl >= 0 ? '#e74c3c' : '#27ae60', fontWeight: pl !== 0 ? 600 : 'normal' }}>
          {pl >= 0 ? '+' : ''}{pl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      );
    }},
    { title: '盈亏比例', key: 'live_plr', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      if (cost > 0 && q) {
        const ratio = (price - cost) / cost * 100;
        return <span style={{ color: ratio >= 0 ? '#e74c3c' : '#27ae60' }}>{ratio >= 0 ? '+' : ''}{ratio.toFixed(2)}%</span>;
      }
      return r.profit_loss_ratio ? `${r.profit_loss_ratio.toFixed(2)}%` : '-';
    }},
    { title: '交易市场', dataIndex: 'market', width: 70, render: v => v ? <Tag>{v}</Tag> : '-' },
    { title: '操作', width: 55, render: (_, r) => {
      const qty = r.available_qty || 0;
      return qty > 0 ? (
        <Button size="small" danger
          onClick={async () => {
            Modal.confirm({
              title: `清仓 ${r.stock_name}(${r.stock_code})`,
              content: `将以买二价卖出全部可用 ${qty} 股`,
              onOk: async () => {
                setSyncingModal({ open: true, label: '清仓' });
                try {
                  const res = await tradingApi.sellAtPriceLevel({
                    account_id: accounts[0]?.id || 'ths_main',
                    client_id: selectedClient?.id || '',
                    stock_code: r.stock_code,
                    stock_name: r.stock_name,
                    quantity: qty,
                    bid_level: 2,
                  });
                  setSyncingModal({ open: false, label: '' });
                  if (res.success) { message.success('清仓委托已提交'); loadPositions(); }
                  else message.error(res.message || '清仓失败');
                } catch { setSyncingModal({ open: false, label: '' }); message.error('清仓请求失败'); }
              },
            });
          }}>
          清仓
        </Button>
      ) : null;
    }},
  ];

  const fillColumns = [
    { title: '日期', dataIndex: 'trade_date', width: 80, render: v => v || '-' },
    { title: '时间', dataIndex: 'trade_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 110, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
    { title: '方向1', dataIndex: 'direction', width: 50, render: v => {
      console.log(v,"ff")
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '成交价', dataIndex: 'price', width: 65, render: v => v?.toFixed(2) },
    { title: '数量', dataIndex: 'quantity', width: 65, render: v => v?.toLocaleString() },
    { title: '金额', dataIndex: 'amount', width: 90, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
    { title: '市场', dataIndex: 'market', width: 65, render: v => v ? <Tag>{v}</Tag> : '-' },
  ];

  const filteredClients = useMemo(() => {
    let list = clients;
    if (filterStatus) list = list.filter(c => c.status === filterStatus);
    if (clientSearch) {
      const s = clientSearch.toLowerCase();
      list = list.filter(c => (c.id || '').toLowerCase().includes(s) || (c.name || '').toLowerCase().includes(s));
    }
    return list;
  }, [clients, filterStatus, clientSearch]);

  const [assetStats, setAssetStats] = useState({ total_asset: 0, total_balance: 0, total_market_value: 0, total_available: 0, total_pl: 0 });
  const loadAssetStats = () => {
    // 后端统一统计当前账号关联客户的总资产（资金+持仓价值）
    accountApi.getAssetStats().then(r => { if (r.success) setAssetStats(r.data || {}); }).catch(() => {});
  };
  useEffect(loadAssetStats, []);

  // 加载分组客户端ID（非管理员只统计自己分组的数据）
  useEffect(() => {
    if (!user || user.is_super_admin === 1 || user.role === 'admin') {
      setGroupClientSet(null);
      return;
    }
    if (!user.group_id) { setGroupClientSet(new Set()); return; }
    groupApi.list().then(r => {
      if (!r.success) return;
      const groups = r.data || [];
      const byId = {};
      const childrenMap = {};
      groups.forEach(g => {
        byId[g.id] = g;
        if (g.parent_id) {
          (childrenMap[g.parent_id] = childrenMap[g.parent_id] || []).push(g);
        }
      });
      const myGroup = byId[user.group_id];
      if (!myGroup) { setGroupClientSet(new Set()); return; }
      // 递归累加主分组及其所有子分组的 client_ids（与后端 getUserGroupClientIds 对齐，使在线人数包含子分组客户）
      const ids = new Set();
      const collect = (g) => {
        let arr = [];
        try { arr = JSON.parse(g.client_ids || '[]'); } catch (e) { arr = []; }
        (arr || []).forEach(id => { if (id) ids.add(id); });
        (childrenMap[g.id] || []).forEach(ch => collect(ch));
      };
      collect(myGroup);
      setGroupClientSet(ids);
    }).catch(() => {});
  }, [user]);

  const onlineCount = (groupClientSet
    ? clients.filter(c => c.status === 'online' && groupClientSet.has(c.id))
    : clients.filter(c => c.status === 'online')
  ).length;
  // 资产统计由后端统一计算返回（当前账号关联客户的资金+持仓价值）
  const totalAsset = assetStats.total_asset || 0;
  const totalMarketValue = assetStats.total_market_value || 0;
  const totalPL = assetStats.total_pl || 0;

  const statusOptions = [
    { label: '全部状态', value: '' },
    { label: '已提交', value: 'submitted' },
    { label: '已成', value: 'filled' },
    { label: '部分', value: 'partially_filled' },
    { label: '已撤', value: 'cancelled' },
    { label: '拒单', value: 'rejected' },
  ];

  const directionOptions = [
    { label: '全部方向', value: '' },
    { label: '买入', value: 'buy' },
    { label: '卖出', value: 'sell' },
  ];

  const tabItems = [
    { key: 'accounts', label: `券商账号 (${accounts.length})`, children: (
      <div>
        <Table dataSource={accounts} columns={accountColumns} rowKey="id" size="small" pagination={false}
          locale={{ emptyText: '选择客户端并点击"账户"同步' }} />
        {editingId && <Space style={{ marginTop: 8 }}><Button type="primary" size="small" onClick={saveAccount}>保存</Button><Button size="small" onClick={() => setEditingId(null)}>取消</Button></Space>}
      </div>
    )},
    { key: 'positions', label: `当前持仓 (${positions.length})`, children: (
      <Row gutter={8}>
        <Col span={6}>
          <Card size="small" bodyStyle={{ padding: 8 }}>
            <Tabs activeKey={tradeForm.direction} onChange={v => { setTradeForm({ ...tradeForm, direction: v, quantity: '' }); setPriceLevelMode(null); }} size="small"
              items={[
                { key: 'buy', label: <span style={{ color: '#e74c3c', fontWeight: 600 }}>买入</span>, children: <div /> },
                { key: 'sell', label: <span style={{ color: '#27ae60', fontWeight: 600 }}>卖出</span>, children: <div /> },
              ]}
              style={{ marginBottom: 4 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div>
                <div style={{ marginBottom: 2, fontSize: 12, color: '#666' }}>股票代码</div>
                <Input placeholder="输入股票代码" value={tradeForm.stockCode} onChange={e => setTradeForm({ ...tradeForm, stockCode: e.target.value })} />
              </div>
              {tradeForm.stockName && <Tag color="blue" style={{ fontSize: 13, padding: '2px 8px' }}>{tradeForm.stockName}</Tag>}
              <div>
                <div style={{ marginBottom: 2, fontSize: 12, color: '#666' }}>委托类型</div>
                <Select value={tradeForm.orderType} onChange={v => setTradeForm({ ...tradeForm, orderType: v, price: v === 'market' ? '' : tradeForm.price })}
                  size="small" style={{ width: '100%' }}
                  options={[
                    { label: '限价', value: 'limit' },
                    { label: '市价', value: 'market' },
                  ]} />
              </div>
              {tradeForm.orderType === 'limit' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 2, fontSize: 12, color: '#666' }}>委托价格</div>
                    <Input placeholder={priceLevelMode ? `按${priceLevelMode === 'buy' ? '卖2' : '买2'}价` : '输入价格'} value={tradeForm.price}
                      disabled={!!priceLevelMode}
                      onChange={e => setTradeForm({ ...tradeForm, price: e.target.value })} />
                  </div>
                  {tradeForm.direction === 'buy' && (
                    <div style={{ flex: '0 0 auto' }}>
                      <div style={{ marginBottom: 2, fontSize: 12, color: '#666' }}>&nbsp;</div>
                      <Button size="small" type={priceLevelMode === 'buy' ? 'primary' : 'default'}
                        onClick={() => {
                          if (priceLevelMode === 'buy') { setPriceLevelMode(null); return; }
                          setPriceLevelMode('buy');
                          setTradeForm({ ...tradeForm, price: '', orderType: 'limit' });
                        }}>
                        {priceLevelMode === 'buy' ? '取消' : '卖2价'}
                      </Button>
                    </div>
                  )}
                  {tradeForm.direction === 'sell' && (
                    <div style={{ flex: '0 0 auto' }}>
                      <div style={{ marginBottom: 2, fontSize: 12, color: '#666' }}>&nbsp;</div>
                      <Button size="small" type={priceLevelMode === 'sell' ? 'primary' : 'default'}
                        onClick={() => {
                          if (priceLevelMode === 'sell') { setPriceLevelMode(null); return; }
                          setPriceLevelMode('sell');
                          setTradeForm({ ...tradeForm, price: '', orderType: 'limit' });
                        }}>
                        {priceLevelMode === 'sell' ? '取消' : '买2价'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div>
                <div style={{ marginBottom: 2, fontSize: 12, color: '#666' }}>委托数量</div>
                <Input placeholder="至少100股（100的倍数）" value={tradeForm.quantity} onChange={e => setTradeForm({ ...tradeForm, quantity: e.target.value })} />
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {tradeForm.direction === 'sell'
                  ? <>可用: <b>{tradeForm.availableQty || 0}</b> 股</>
                  : <>可用资金: <b style={{ color: '#27ae60' }}>{(accounts[0]?.available || 0).toLocaleString()}</b> 元</>}
              </div>
              <Button type="primary" block htmlType="button"
                style={{ background: tradeForm.direction === 'buy' ? '#e74c3c' : '#27ae60', borderColor: tradeForm.direction === 'buy' ? '#e74c3c' : '#27ae60' }}
                onClick={() => handleTrade(tradeForm.direction)}>
                {tradeForm.direction === 'buy' ? '买入' : '卖出'}
              </Button>
              <Button block size="small" onClick={() => setTradeForm({ orderType: 'limit', stockCode: '', stockName: '', price: '', quantity: '', direction: tradeForm.direction, availableQty: 0, currentPrice: 0 })}>清空</Button>
              <small style={{ color: '#999', textAlign: 'center', display: 'block' }}>点击持仓行自动填入</small>
            </div>
          </Card>
        </Col>
        <Col span={18}>
          <Table dataSource={positions} columns={posColumns} rowKey="id" size="small" pagination={false}
            locale={{ emptyText: '暂无持仓数据，点击"持仓"同步' }}
            onRow={(r) => ({ onClick: () => onSelectPosition(r), style: { cursor: 'pointer' } })} />
        </Col>
      </Row>
    )},
    { key: 'todayOrders', label: `当日委托 (${todayOrders.length})`, children: (
      <Table dataSource={todayOrders} columns={orderColumns} rowKey="id" size="small" pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '30', '50'] }} scroll={{ y: 350 }}
        locale={{ emptyText: '点击客户端"当日委托"按钮同步' }} />
    )},
    { key: 'screenshots', label: `截图记录 (${screenshots.length})`, children: (
      <div>
        <div style={{ marginBottom: 8, textAlign: 'right' }}>
          <Popconfirm title="确认删除全部截图？" okText="删除" cancelText="取消"
            onConfirm={async () => {
              try {
                const res = await clientApi.deleteAllScreenshots(selectedClient?.id);
                if (res.success) { message.success('已删除全部截图'); loadScreenshots(); }
                else message.error(res.message || '删除失败');
              } catch { message.error('删除失败'); }
            }}>
            <Button size="small" danger disabled={screenshots.length === 0} icon={<DeleteTwoTone />}>全部删除</Button>
          </Popconfirm>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: 4 }}>
          {screenshots.map(s => (
            <div key={s.id} style={{ textAlign: 'center' }}>
              <div style={{ cursor: 'pointer' }}
                onClick={() => window.open(`${config.API_URL}/screenshots/file/${s.id}`, '_blank')}>
                <img src={`${config.API_URL}/screenshots/file/${s.id}`} alt="截图" style={{ width: 200, height: 150, objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} />
              </div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{new Date(s.created_at).toLocaleString()}</div>
              <Popconfirm title="确认删除该截图？" okText="删除" cancelText="取消"
                onConfirm={async () => {
                  try {
                    const res = await clientApi.deleteScreenshot(s.id);
                    if (res.success) { message.success('已删除'); loadScreenshots(); }
                    else message.error(res.message || '删除失败');
                  } catch { message.error('删除失败'); }
                }}>
                <Button size="small" danger type="text" icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>
            </div>
          ))}
          {screenshots.length === 0 && <div style={{ color: '#999', padding: 40 }}>暂无截图记录</div>}
        </div>
      </div>
    )},
    { key: 'orders', label: `历史委托 (${filteredOrders.length})`, children: (
      <div>
        <Space style={{ marginBottom: 8 }} wrap>
          <Input.Search placeholder="搜索股票" value={orderSearch} onChange={e => setOrderSearch(e.target.value)} allowClear style={{ width: 130 }} />
          <Input type="date" value={orderDateFrom} onChange={e => setOrderDateFrom(e.target.value)} style={{ width: 130 }} />
          <span style={{ color: '#999' }}>~</span>
          <Input type="date" value={orderDateTo} onChange={e => setOrderDateTo(e.target.value)} style={{ width: 130 }} />
          <Select value={orderDirection} onChange={setOrderDirection} options={directionOptions} style={{ width: 85 }} />
          <Select value={orderStatus} onChange={setOrderStatus} options={statusOptions} style={{ width: 85 }} />
        </Space>
        <Table dataSource={filteredOrders} columns={historyOrderColumns} rowKey="id" size="small" pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '30', '50'] }} scroll={{ y: 350 }} />
      </div>
    )},
    { key: 'fills', label: `历史成交 (${filteredFills.length})`, children: (
      <div>
        <Space style={{ marginBottom: 8 }} wrap>
          <Input.Search placeholder="搜索股票" value={fillSearch} onChange={e => setFillSearch(e.target.value)} allowClear style={{ width: 130 }} />
          <Input type="date" value={fillDateFrom} onChange={e => setFillDateFrom(e.target.value)} style={{ width: 130 }} />
          <span style={{ color: '#999' }}>~</span>
          <Input type="date" value={fillDateTo} onChange={e => setFillDateTo(e.target.value)} style={{ width: 130 }} />
          <Select value={fillDirection} onChange={setFillDirection} options={directionOptions} style={{ width: 85 }} />
        </Space>
        <Table dataSource={filteredFills} columns={fillColumns} rowKey="id" size="small" pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '30', '50'] }} scroll={{ y: 350 }} />
      </div>
    )},
    { key: 'deliveries', label: `交割单 (${deliveries.length})`, children: (
      <div>
        <Space style={{ marginBottom: 8 }} wrap>
          <Input.Search placeholder="搜索股票" value={deliverySearch} onChange={e => setDeliverySearch(e.target.value)} allowClear style={{ width: 140 }} />
          <Select value={deliveryDirection} onChange={setDeliveryDirection} allowClear placeholder="全部方向" options={directionOptions} style={{ width: 100 }} />
          <Input type="date" value={deliveryDateFrom} onChange={e => setDeliveryDateFrom(e.target.value)} style={{ width: 140 }} />
          <span style={{ color: '#999' }}>~</span>
          <Input type="date" value={deliveryDateTo} onChange={e => setDeliveryDateTo(e.target.value)} style={{ width: 140 }} />
        </Space>
        {(() => {
          const sum = filteredDeliveries.reduce((a, r) => ({
            commission: a.commission + (Number(r.commission) || 0),
            transfer_fee: a.transfer_fee + (Number(r.transfer_fee) || 0),
            stamping_tax: a.stamping_tax + (Number(r.stamping_tax) || 0),
            net_commission: a.net_commission + (Number(r.net_commission) || 0),
            real_amount: a.real_amount + (Number(r.real_amount) || 0),
          }), { commission: 0, transfer_fee: 0, stamping_tax: 0, net_commission: 0, real_amount: 0 });
          const item = (label, val, color = '#cf1322') => (
            <span style={{ marginRight: 24 }}>{label}: <b style={{ color }}>{(val || 0).toFixed(2)}</b></span>
          );
          return (
            <div style={{ marginBottom: 8, padding: '8px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 13 }}>
              {item('手续费合计', sum.commission)}
              {item('过户费合计', sum.transfer_fee)}
              {item('印花税合计', sum.stamping_tax)}
              {item('净佣金合计', sum.net_commission)}
              {item('发生金额合计', sum.real_amount)}
              <span style={{ color: '#999' }}>（共 {filteredDeliveries.length} 笔）</span>
            </div>
          );
        })()}
        <Table dataSource={filteredDeliveries} rowKey="id" size="small" scroll={{ x: 1300, y: 350 }}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'], showTotal: t => `共 ${t} 条` }} bordered
          columns={[
            { title: '成交日期', dataIndex: 'trade_date', width: 100 },
            { title: '成交时间', dataIndex: 'trade_time_str', width: 90 },
            { title: '代码', dataIndex: 'stock_code', width: 80 },
            { title: '名称', dataIndex: 'stock_name', width: 90 },
            { title: '操作', dataIndex: 'direction', width: 60, render: v => v === 'buy' ? <Tag color="#cf1322">买入</Tag> : v === 'sell' ? <Tag color="#3f8600">卖出</Tag> : (v || '-') },
            { title: '数量', dataIndex: 'quantity', width: 70, align: 'right' },
            { title: '价格', dataIndex: 'price', width: 80, align: 'right', render: v => v ? v.toFixed(3) : '-' },
            { title: '成交金额', dataIndex: 'amount', width: 100, align: 'right', render: v => v ? v.toFixed(2) : '-' },
            { title: '余额', dataIndex: 'balance', width: 100, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
            { title: '发生金额', dataIndex: 'real_amount', width: 100, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
            { title: '印花税', dataIndex: 'stamping_tax', width: 80, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
            { title: '手续费', dataIndex: 'commission', width: 80, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
            { title: '资金账户', dataIndex: 'capital_account', width: 90 },
            { title: '合同编号', dataIndex: 'contract_no', width: 130 },
            { title: '市场', dataIndex: 'market', width: 80 },
          ]} />
      </div>
    )},
  ];

  return (
    <div>
      {apiDown && (
        <Card size="small" style={{ marginBottom: 12, borderLeft: '3px solid #faad14' }}>
          <Space><Badge status="warning" /> 后端服务连接异常，正在自动重连...</Space>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        <Col span={4}><Card><Statistic title="客户端在线" value={onlineCount} suffix={`/ ${clients.length}`} prefix={<LaptopOutlined />} valueStyle={{ color: onlineCount > 0 ? '#52c41a' : '#999' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="总资产" value={totalAsset} precision={2} suffix="元" valueStyle={{ color: '#2980b9' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="可用资金" value={assetStats.total_available || 0} precision={2} suffix="元" valueStyle={{ color: '#27ae60' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="持仓总市值" value={totalMarketValue} precision={2} suffix="元" valueStyle={{ color: '#e67e22' }} /></Card></Col>
        {isAdmin && <Col span={5}><Card><Statistic title="总持仓盈亏" value={totalPL} precision={2} suffix="元" valueStyle={{ color: totalPL >= 0 ? '#e74c3c' : '#27ae60' }} /></Card></Col>}
      </Row>

      <Card
        title={<Space><LaptopOutlined /> 客户端列表 <Tag>在线 {onlineCount}/{clients.length}</Tag></Space>}
        size="small" style={{ marginTop: 12 }}
        extra={<Space>
          <Button size="small" onClick={() => openSyncConfirm('positions', '同步资金持仓')}>同步资金持仓</Button>
          <Button size="small" onClick={() => openSyncConfirm('orders', '同步当日委托')}>同步当日委托</Button>
          <Button size="small" onClick={() => openSyncConfirm('deliveries', '同步交割单(近一年)')}>一键同步交割单</Button>
          <Popconfirm title="确认删除所有截图？" okText="删除" cancelText="取消"
            onConfirm={async () => {
              try {
                const res = await clientApi.deleteAllScreenshots();
                if (res.success) { message.success('已删除全部截图'); if (loadScreenshots) loadScreenshots(); }
                else message.error(res.message || '删除失败');
              } catch { message.error('删除失败'); }
            }}>
            <Button size="small" danger icon={<DeleteOutlined />}>一键删除截图</Button>
          </Popconfirm>
          <Button size="small" icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
        </Space>}
      >
        <Tabs size="small" activeKey={filterStatus || 'all'} onChange={k => setFilterStatus(k === 'all' ? '' : k)} items={[
          { key: 'all', label: `全部 (${clients.length})` },
          { key: 'online', label: `在线 (${clients.filter(c => c.status === 'online').length})` },
          { key: 'offline', label: `离线 (${clients.filter(c => c.status !== 'online').length})` },
        ]} />
        <Input.Search placeholder="搜索客户端名称或ID" value={clientSearch} onChange={e => setClientSearch(e.target.value)}
          style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
        <Table
          dataSource={filteredClients} columns={clientColumns} rowKey="id" size="small"
          pagination={{
            current: clientPage.current,
            pageSize: clientPage.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: t => `共 ${t} 台`,
            onChange: (current, pageSize) => setClientPage({ current, pageSize }),
          }}
          locale={{ emptyText: '暂无客户端' }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedClient ? [selectedClient.id] : [],
            onChange: (keys, rows) => { if (rows.length > 0) setSelectedClient(rows[0]); },
            getCheckboxProps: (r) => ({ disabled: r.status !== 'online' }),
          }}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                <Descriptions size="small" column={3}>
                  <Descriptions.Item label="客户端ID"><code>{r.id}</code></Descriptions.Item>
                  <Descriptions.Item label="客户端名称">{r.name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="券商">{r.broker || '-'}</Descriptions.Item>
                  <Descriptions.Item label="远程桌面账号">{r.sunflower_account || '-'}</Descriptions.Item>
                  <Descriptions.Item label="远程桌面密码">{r.sunflower_pass || '-'}</Descriptions.Item>
                  <Descriptions.Item label="MAC 地址">{r.mac_addr || '-'}</Descriptions.Item>
                  <Descriptions.Item label="机器ID">{r.machine_id ? <code style={{ fontSize: 11 }}>{r.machine_id}</code> : '-'}</Descriptions.Item>
                  <Descriptions.Item label="操作系统">{r.os_version || '-'}</Descriptions.Item>
                  <Descriptions.Item label="CPU">{r.cpu_info || '-'}</Descriptions.Item>
                  <Descriptions.Item label="内存">{r.memory_total || '-'}</Descriptions.Item>
                </Descriptions>
                <Space style={{ marginTop: 8 }}>
                  {isAdmin && (
                    <Button size="small" disabled={r.status !== 'online'} onClick={async () => {
                      Modal.confirm({ title: '确认重启', content: `确定要重启 ${r.name || r.id} 的同花顺客户端？`, onOk: async () => {
                        message.loading({ content: '正在终止同花顺...', key: 'restart' });
                        try {
                          await clientApi.killXiadan(r.id);
                          message.loading({ content: '正在启动同花顺...', key: 'restart' });
                          await clientApi.startXiadan(r.id, r.sunflower_pass || '');
                          message.destroy('restart');
                          message.success('同花顺已重启');
                        } catch { message.destroy('restart'); message.error('重启失败'); }
                      }});
                    }}>重启同花顺</Button>
                  )}
                  {isAdmin && (
                    <Button size="small" disabled={r.status !== 'online'} onClick={async () => {
                      const r2 = await clientApi.closePopup(r.id);
                      if (r2.success) message.success('关闭指令已发送'); else message.error(r2.message || '发送失败');
                    }}>关闭弹窗</Button>
                  )}
                  {isAdmin && (
                    <Button size="small" danger onClick={() => Modal.confirm({ title: '确认删除', content: `确定删除客户端 ${r.name || r.id}？`, onOk: async () => { const r2 = await clientApi.deleteClient(r.id); if (r2.success) { message.success('已删除'); loadData(); } else message.error(r2.message) } })}>删除</Button>
                  )}
                </Space>
              </div>
            ),
            rowExpandable: () => true,
          }}
        />
      </Card>

      <Card
        title={selectedClient
          ? <Space><Badge status={selectedClient.status === 'online' ? 'success' : 'default'} /> {selectedClient.name} - 详情</Space>
          : <span style={{ color: '#999' }}>请选择一个客户端查看详情</span>}
        size="small" style={{ marginTop: 12 }} bodyStyle={{ padding: selectedClient ? 0 : 24 }}
      >
        {selectedClient ? (
          <Tabs activeKey={activeTab} onChange={onTabChange} items={tabItems} size="small" style={{ margin: '0 8px' }} />
        ) : (
          <div style={{ textAlign: 'center', color: '#999' }}>点击上方客户端列表选中设备</div>
        )}
      </Card>

      <Modal title="编辑客户端" open={renameModal.open}
        onOk={handleRename} onCancel={() => setRenameModal({ open: false, clientId: null, name: '', sunflower_account: '', sunflower_pass: '', broker: '' })} okText="保存" cancelText="取消" width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 3 }}>客户端名称</div>
            <Input value={renameModal.name} onChange={e => setRenameModal({ ...renameModal, name: e.target.value })} placeholder="输入名称" maxLength={50} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 3 }}>远程桌面账号</div>
            <Input value={renameModal.sunflower_account} onChange={e => setRenameModal({ ...renameModal, sunflower_account: e.target.value })} placeholder="输入远程桌面账号" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 3 }}>远程桌面密码</div>
            <Input value={renameModal.sunflower_pass} onChange={e => setRenameModal({ ...renameModal, sunflower_pass: e.target.value })} placeholder="输入远程桌面密码" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 3 }}>券商</div>
            <Input value={renameModal.broker} onChange={e => setRenameModal({ ...renameModal, broker: e.target.value })} placeholder="输入券商名称" />
          </div>
        </div>
      </Modal>
      <Modal title={null} open={syncingModal.open} closable={false} keyboard={false} maskClosable={false} footer={null}
        width={260} centered>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>正在{syncingModal.label}...</div>
          <div style={{ marginTop: 8, fontSize: 13, color: '#999' }}>请勿操作，执行中...</div>
        </div>
      </Modal>
      <Modal title="一键同步" open={syncBatch.open} closable={syncBatch.done} keyboard={false} maskClosable={false}
        footer={syncBatch.done ? <Button type="primary" onClick={() => setSyncBatch({ open: false, progress: '', done: false })}>确定</Button> : null} width={360} centered>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {!syncBatch.done && <Spin />}
          <div style={{ marginTop: 12, fontSize: 15 }}>{syncBatch.progress}</div>
        </div>
      </Modal>
      {/* 同步弹窗（确认列表 / 执行中 / 完成） */}
      <Modal
        title={syncResult.mode === 'confirm' ? `${syncResult.title} - 待同步客户端` : `${syncResult.title}${syncResult.status === 'running' ? '（进行中...）' : '（完成）'}`}
        open={syncResult.open}
        onCancel={() => setSyncResult(prev => ({ ...prev, open: false }))}
        footer={
          syncResult.mode === 'confirm' ? (
            <Space>
              <Button onClick={() => setSyncResult(prev => ({ ...prev, open: false }))}>取消</Button>
              <Button type="primary" disabled={syncResult.clientList.length === 0} onClick={executeSync}>开始同步</Button>
            </Space>
          ) : (
            <Button type="primary" onClick={() => setSyncResult(prev => ({ ...prev, open: false }))}>关闭</Button>
          )
        }
        width={560}
      >
        {syncResult.message ? (
          <div style={{ padding: 16, color: '#888' }}>{syncResult.message}</div>
        ) : syncResult.mode === 'confirm' ? (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Tag color="blue">在线 {syncResult.total} 台</Tag>
              <Tag color="default">离线 {clients.filter(c => c.status !== 'online').length} 台</Tag>
            </div>
            <div style={{ maxHeight: 360, overflow: 'auto' }}>
              {syncResult.clientList.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>当前没有在线客户端</div>
              ) : (
                syncResult.clientList.map((c, idx) => (
                  <div key={idx} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 13,
                  }}>
                    <span>{c.client_name}</span>
                    <Tag color="green">在线</Tag>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Tag color="blue">共 {syncResult.total} 台</Tag>
              <Tag color="green">成功 {syncResult.success}</Tag>
              <Tag color="red">失败 {syncResult.fail}</Tag>
              {syncResult.status === 'running' && <Spin size="small" style={{ marginLeft: 8 }} />}
            </div>
            <div style={{ maxHeight: 360, overflow: 'auto' }}>
              {(syncResult.results || []).map((r, idx) => {
                let statusTag;
                if (r.status === 'running') {
                  statusTag = <span><Spin size="small" /> <Tag color="processing">同步中</Tag></span>;
                } else if (r.status === 'success' || r.success) {
                  statusTag = <Tag color="green">成功</Tag>;
                } else if (r.status === 'pending') {
                  statusTag = <Tag color="default">等待</Tag>;
                } else {
                  statusTag = <Tag color="red">{r.message || '失败'}</Tag>;
                }
                return (
                  <div key={idx} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 13,
                  }}>
                    <span>{r.client_name || r.client_id}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Tag color={r.account_synced ? 'success' : 'error'} style={{ marginRight: 0 }}>资金{r.account_synced ? '✓' : '✗'}</Tag>
                      <Tag color={r.position_synced ? 'success' : 'error'} style={{ marginRight: 0 }}>持仓{r.position_synced ? '✓' : '✗'}</Tag>
                      {statusTag}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
